import axios from "axios";

// Hard-fail at build time if REACT_APP_BACKEND_URL wasn't set during the
// React build. Without this, the bundle gets baked with `undefined/api` which
// Capacitor's file:// scheme silently resolves to the bundled index.html on
// every API call — axios sees 200 OK with HTML body and your "successful"
// upload never reaches the backend. This caused 8 hours of debugging on iOS
// TestFlight build 31-36 before I found it (the .env file is gitignored, so
// Codemagic builds had no idea what the backend URL was). Codemagic now writes
// the .env file explicitly in its `Write production env` step.
const RAW_BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
if (!RAW_BACKEND_URL || RAW_BACKEND_URL === "undefined") {
  console.error(
    "[Hush] CRITICAL: REACT_APP_BACKEND_URL is not set at build time. " +
    "API calls will silently fail by hitting the bundled index.html. " +
    "Falling back to production https://apphush.online so the app still works."
  );
}
const BACKEND_URL = RAW_BACKEND_URL && RAW_BACKEND_URL !== "undefined"
  ? RAW_BACKEND_URL
  : "https://apphush.online";

export const API = `${BACKEND_URL}/api`;

// Expose the API URL globally so diagnostic telemetry can include which
// backend the build is configured to hit (preview vs production).
if (typeof window !== "undefined") {
  window.__HUSH_API_URL__ = API;
}

// Bearer token store — used on native (capacitor://) where cross-origin
// cookies are unreliable in WKWebView. On web we still rely on cookies; the
// bearer header is just additive insurance.
const TOKEN_KEY = "hush_session_token";
export function getSessionToken() {
  try {
    return typeof window !== "undefined" ? window.localStorage?.getItem(TOKEN_KEY) || "" : "";
  } catch (_) { return ""; }
}
export function setSessionToken(token) {
  try {
    if (typeof window === "undefined") return;
    if (token) window.localStorage?.setItem(TOKEN_KEY, token);
    else window.localStorage?.removeItem(TOKEN_KEY);
  } catch (_) { /* private mode — silently ignored */ }
}

export const api = axios.create({
  baseURL: API,
  withCredentials: true,
  // Hard cap so the iPad WKWebView never sits forever on a stalled request.
  // 10s is plenty for a healthy backend; anything beyond that means trouble
  // and the UI should fall back to the unauthenticated state.
  timeout: 10000,
});

// Attach the bearer token (if we have one) to every request. Backend accepts
// both `Cookie: session_token=...` and `Authorization: Bearer ...`.
api.interceptors.request.use((config) => {
  const token = getSessionToken();
  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Capture the token from auth endpoints' responses (register, login, google,
// apple) so subsequent requests can authenticate via header.
api.interceptors.response.use(
  (resp) => {
    try {
      const tok = resp?.data?.session_token;
      if (tok && typeof tok === "string") {
        setSessionToken(tok);
      }
    } catch (_) { /* noop */ }
    return resp;
  },
  (err) => Promise.reject(err)
);

export function formatApiError(err) {
  const detail = err?.response?.data?.detail;
  if (detail == null) return err?.message || "Something went wrong";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export const MOOD_COLORS = {
  happy: "#DDA754",
  sad: "#8A87A1",
  anxious: "#B59A6B",
  calm: "#7FA3C1",
  excited: "#E08F5B",
  angry: "#C86653",
  neutral: "#A39E92",
  reflective: "#7A6E8C",
};

export function moodColor(m) {
  return MOOD_COLORS[m] || MOOD_COLORS.neutral;
}
